import type { ElsewhereCard, ParcelCard } from 'shared/api'
import type { useTranslate } from 'shared/lib/i18n'

/**
 * The words a person reads for the server's lookup answers (ticket 48): a holder,
 * where a thing is now up its holders, «в пункте X / в пути / не в сети», who did a
 * history row and its cause. Pure functions of the answer and the screen's words.
 */

export type WhereNowAnswer = NonNullable<ParcelCard['whereNow']>
export type HolderName = WhereNowAnswer['chain'][number]
type Translate = ReturnType<typeof useTranslate>['t']

/** One holder as a person reads it. */
export function holderWords(t: Translate, h: HolderName): string {
	switch (h.type) {
		case 'location':
			return t(h.locationType === 'hub' ? 'where.atHub' : h.locationType === 'point' ? 'where.atPoint' : 'where.atPlace', {
				id: h.id || '—',
			})
		case 'driver':
			return t('where.withDriver', { name: h.name || '—' })
		case 'container':
			return t(h.kind === 'partner_box' ? 'where.inBox' : h.kind === 'cash_bag' ? 'where.inCashBag' : 'where.inBag', {
				code: h.code,
			})
	}
}

/** The chain read from the place outward in: the place or the driver first, then what it holds. */
export function whereNowWords(t: Translate, where: WhereNowAnswer): string {
	const parts = [...where.chain].reverse().map((h) => holderWords(t, h))
	if (where.trip) parts.push(t('where.trip', { code: where.trip.code }))
	return parts.join(' · ')
}

/** The answer about what is not the operator's: «в пункте ID», «в пути», «не в сети». */
export function elsewhereWords(t: Translate, e: Pick<ElsewhereCard, 'where' | 'point'>): string {
	if (e.where === 'at_point') {
		return t('where.elsewhere_at_point', { id: e.point?.id || '—' })
	}
	return t(e.where === 'in_transit' ? 'where.elsewhere_in_transit' : 'where.elsewhere_not_in_network')
}

export type HistoryEntry = ParcelCard['history'][number]

/** The causes the screen has words for. */
const CAUSES = new Set([
	'manifest',
	'manifest_file',
	'partner_command',
	'support',
	'support_edit',
	'sort',
	'counter',
	'return_flow',
	'hub_shelf_decision',
	'storage_expiry',
	'shadow',
	'seal',
	'point_accept',
	'partner_handover',
	'open',
	'found',
	'finish',
	'double_payment_activated',
	'disposal',
	'discrepancy',
	'damage',
	'count',
	'container',
	'cash_bag',
	'cash_bag_pickup',
	'cash_bag_receipt',
	'box_check',
	'trip',
	'trip_pickup',
	'bag',
	'box',
	'migration',
	'point_rollback',
	'inventory',
])

/** Who did it, as a person reads it. */
export function actorWords(t: Translate, entry: HistoryEntry): string {
	switch (entry.actor.type) {
		case 'staff':
			return entry.actorRole
				? t('historyRow.staff', { name: entry.actorName || '—', role: t(`roles.${entry.actorRole}`) })
				: (entry.actorName ?? t('parcelActor.staff'))
		case 'partner':
			return t('historyRow.partner', { name: entry.actorName || '—' })
		case 'system':
			return t('parcelActor.system')
		case 'translator':
			return t('parcelActor.translator')
	}
}

/**
 * The cause in words, with what it names. A cause the screen has no words for is not
 * shown as its raw code: only what it names, or nothing.
 */
export function causeWords(t: Translate, entry: HistoryEntry): string {
	const words = CAUSES.has(entry.cause.type) ? t(`parcelCause.${entry.cause.type}` as never) : ''
	return [words, entry.causeLabel].filter(Boolean).join(' · ')
}

/** A sum in sums, its thousands apart: `25 000`. */
export function sum(n: number): string {
	return n.toLocaleString('ru-RU')
}

/** An amount in its currency as a person reads it: sums as «25 000 сум», any other with its code. */
export function money(t: Translate, m: { amount: number; currency: string }): string {
	return m.currency === 'UZS' ? t('payment.som', { amount: sum(m.amount) }) : `${sum(m.amount)} ${m.currency}`
}
